/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */


import ImageDataLoader from "App/utils/textures/image_data_loader";
import TextureDataHandler from "App/utils/textures/texture_data_handler";
import {EDataTypeType} from "App/geometry/material/texture";
import {logInfo} from "App/debug";
import ImageDataLoadersFactory from "App/utils/textures/image_data_loaders_factory";
import WebGLContext from "App/web_gl_context";

export default
class PVRImageDataLoader extends ImageDataLoader {

    public static TypeName: string = EDataTypeType.pvrImageTexture;

    constructor(aTextureDataHandler: TextureDataHandler, aDiffuseTextureProperties: any, aLevel: number) {
        super(aTextureDataHandler, aDiffuseTextureProperties, aLevel);
    }

    public static create(aTextureDataHandler, aDiffuseTextureProperties, aLevel): PVRImageDataLoader {
        return new PVRImageDataLoader(aTextureDataHandler, aDiffuseTextureProperties, aLevel);
    }

    public static getSizeFromResolution(aResolution: number, aBlockSize: number): number {
        let blockSize = 0;
        let widthBlocks = 0;
        let heightBlocks = 0;
        let bpp = 0;

        if (aBlockSize === 4) {
            blockSize = 4 * 4; // Pixel by pixel block size for 4bpp
            widthBlocks = Math.floor((aResolution / 4) + 0.5);
            heightBlocks = Math.floor((aResolution / 4) + 0.5);
            bpp = 4;
        } else {
            blockSize = 8 * 4; // Pixel by pixel block size for 2bpp
            widthBlocks = Math.floor((aResolution / 8) + 0.5);
            heightBlocks = Math.floor((aResolution / 4) + 0.5);
            bpp = 2;
        }

        // Clamp to minimum number of blocks
        if (widthBlocks < 2) {
            widthBlocks = 2;
        }

        if (heightBlocks < 2) {
            heightBlocks = 2;
        }

        //logInfo("res: " + aResolution + ", size: " + widthBlocks * heightBlocks * ((blockSize  * bpp) / 8));
        return widthBlocks * heightBlocks * ((blockSize * bpp) / 8);
    }


    private parseBuffer(aBuffer: Uint8Array): any {

        let buffer = new DataView(aBuffer.buffer, aBuffer.byteOffset);

        let pos: number = 0;

        // lagacy pvr header
        let headerLength = buffer.getInt32(pos, true); pos += 4;
        let height = buffer.getInt32(pos, true); pos += 4;
        let width = buffer.getInt32(pos, true); pos += 4;
        let numMipmaps = buffer.getInt32(pos, true); pos += 4;
        let flags = buffer.getInt32(pos, true); pos += 4; //including pixel format
        let dataLength = buffer.getInt32(pos, true); pos += 4;
        let bpp = buffer.getInt32(pos, true); pos += 4;
        let bitmaskRed = buffer.getInt32(pos, true); pos += 4;
        let bitmaskGreen = buffer.getInt32(pos, true); pos += 4;
        let bitmaskBlue = buffer.getInt32(pos, true); pos += 4;
        let bitmaskAlpha = buffer.getInt32(pos, true); pos += 4;

        //let pvrTag = buffer.readString(4);
        pos += 4; // this is for four ascii chars i.e. 4 bytes

        let numSurfs = buffer.getInt32(pos, true); pos += 4;

        let TYPE_MASK = 0xff;
        let PVRTC_2 = 24;
        let PVRTC_4 = 25;

        let formatFlags = flags & TYPE_MASK;

        let format;
        let _hasAlpha = bitmaskAlpha > 0;

        let pvrtc = WebGLContext.getFeatureAsExtension("COMPRESSED_TEXTURE_PVRTC");
        if (formatFlags === PVRTC_4) {
            format = _hasAlpha ? pvrtc.COMPRESSED_RGBA_PVRTC_4BPPV1_IMG : pvrtc.COMPRESSED_RGB_PVRTC_4BPPV1_IMG;
            bpp = 4;
        } else if (formatFlags === PVRTC_2) {
            format = _hasAlpha ? pvrtc.COMPRESSED_RGBA_PVRTC_2BPPV1_IMG : pvrtc.COMPRESSED_RGB_PVRTC_2BPPV1_IMG;
            bpp = 2;
        } else {
            logInfo("ERROR: pvrtc - unknown format " + formatFlags);
            return;
        }

        let pvr = {
            mipmaps: [],
            width: width,
            height: height,
            format: format,
            hasAlpha: _hasAlpha,
            bpp: bpp,
            mipmapCount: numMipmaps + 1,
            isCubemap: (numSurfs === 6)
        };

        let dataSize = 0;
        let blockSize = 0;
        let blockWidth = 0;
        let blockHeight = 0;
        let widthBlocks = 0;
        let heightBlocks = 0;

        if (bpp === 2) {
            blockWidth = 8;
            blockHeight = 4;
        } else {
            blockWidth = 4;
            blockHeight = 4;
        }

        blockSize = (blockWidth * blockHeight) * bpp / 8;

        pvr.mipmaps.length = pvr.mipmapCount * numSurfs;

        let mipLevel = 0;
        while (mipLevel < pvr.mipmapCount) {

            let sWidth = pvr.width >> mipLevel;
            let sHeight = pvr.height >> mipLevel;

            widthBlocks = sWidth / blockWidth;
            heightBlocks = sHeight / blockHeight;

            // Clamp to minimum number of blocks
            if (widthBlocks < 2) {
                widthBlocks = 2;
            }

            if (heightBlocks < 2) {
                heightBlocks = 2;
            }

            dataSize = widthBlocks * heightBlocks * blockSize;

            for (let surfIndex = 0; surfIndex < numSurfs; surfIndex++) {

                let data = new Uint8Array(dataSize);//buffer.buffer.slice(pos, pos + dataSize));
                data.set(new Uint8Array(buffer.buffer, buffer.byteOffset + pos, dataSize));
                pos += dataSize;

                let mipmap: any = {
                    data: data,
                    width: Math.max(1, sWidth),
                    height: Math.max(1, sHeight)
                };

                pvr.mipmaps[surfIndex * pvr.mipmapCount + mipLevel] = mipmap;
            }

            mipLevel++;
        }

        return pvr;
    }

    public prepareData() {
        if (this._loadIterator >= this.tiles.length) {
            return;
        }

        let tile = this.tiles[this._loadIterator];

        // we need only init the width and height here
        tile.width = this._diffuseTextureProperties.width;
        tile.height = this._diffuseTextureProperties.height;

        tile.data = this.parseBuffer(tile.compressedUint8Data);

        // here we test if we specify the texture using only the url wihtout knowing the size of it
        // it this case we re-init the properties based on th reloaded dom image object
        if (!this._diffuseTextureProperties.tiled &&
            this._diffuseTextureProperties.width === 0 &&
            this._diffuseTextureProperties.height === 0) {
            tile.width = tile.data.width;
            tile.height = tile.data.height;
            this._diffuseTextureProperties.width = tile.width;
            this._diffuseTextureProperties.height = tile.height;
        }

        tile.ready = true;

        // force the alpha according to the format
        this._diffuseTextureProperties.alpha = tile.data.hasAlpha;

        this._loadIterator++;
    }
}

ImageDataLoadersFactory.register(PVRImageDataLoader);